import {
  ButtonIcon,
  CardWrapperWithHeader,
  chartProducts,
  csvExportConfigs,
  withCsvHeaders,
  FiltersModal,
  FilterValueShape,
  getClaimLimit,
} from '@rabbit/elements/shared-components';
import { useContext, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  formatDataChart,
  getUserPermissions,
  ONCE,
  titleCase,
} from '../utils/helpers';
import {
  DTHoldingProxy,
  Permissions,
  VehicleInfo,
  Warranty,
  WarrantyStatus,
} from '@rabbit/data/types';
import { useGetHoldingList } from '@rabbit/bizproc/react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { FunnelIcon } from '@heroicons/react/24/outline';
import {
  Config_ClaimDetailViewType,
  SAGE_ROUTE_NAME,
} from '@rabbit/config/enums';
import { useNavigate } from 'react-router-dom';
import { CreateTable, TableFields } from '../utils/CreateTable';
import { AppContext } from '@rabbit/app-context';
import { BL_Warranty } from '@rabbit/bizproc/core';
import i18next from 'i18next';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';

type WarrantyOverviewShape = Warranty & { holding: DTHoldingProxy };

// Warranty status calculation
const getWarrantyStatus = (warranty: Warranty | null | undefined) => {
  if (!warranty) return undefined;
  const expiredDate = new Date().setHours(-1); // at 23:00 last day
  const upcomingDate = Date.now() + 2 * 30.14 * 24 * 3600 * 1000;

  const expired = warranty?.endDate && warranty?.endDate < expiredDate;
  const upcoming =
    warranty?.startDate &&
    warranty?.startDate > Date.now() &&
    warranty?.startDate < upcomingDate;

  return upcoming
    ? 'upcoming'
    : expired
    ? 'expired'
    : warranty?.status?.toLowerCase();
};

export default function WarrantyOverviewView() {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const tenantLink = t('tenantLink');
  const userPermissions = getUserPermissions(tenantLink, userContext);
  const navigate = useNavigate();
  const { tenantInfo, config } = useContext(AppContext);
  const { accessToken, repairerPersonaId, retailerPersonaId } =
    userContext ?? ({} as any);

  const activePersonaIds = {
    repairer: repairerPersonaId,
    retailer: retailerPersonaId,
  };

  const [total, setTotal] = useState(0);
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const beginDate = new Date(currentYear, currentMonth, 1);
  const endDate = today;

  const monthlyBeginDate = moment(endDate).subtract(1, 'months').toDate();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [chartStatusData, setChartStatusData] = useState<any[]>([]);
  const [chartTemplateData, setChartTemplateData] = useState<any[]>([]);
  const CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT =
    t('CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT') === 'true';

  const [filters, setFilters] = useState<FilterValueShape>(() => {
    if (t('tenantLink') === 'WARRANTYIRELAND') {
      return {
        dateType: 'This month',
        beginDate: beginDate,
        endDate: endDate,
      };
    }

    if (config.HOLDINGS.REGISTRATION.SHOW_MONTHLY_DATA) {
      return {
        dateType: 'Date Range',
        beginDate: monthlyBeginDate,
        endDate: endDate,
      };
    }

    return {};
  });

  const { holdingList, isLoading } = useGetHoldingList(
    t('tenantLink'),
    accessToken,
    activePersonaIds,
    filters
  );

  const onChangeFilters = (value: FilterValueShape) => {
    setFilters(value);
    setIsFilterModalOpen(false);
  };

  const warrantyFilters = (holding: DTHoldingProxy) =>
    (holding.warranties || []).filter((w) => {
      const status = getWarrantyStatus(w);
      return filters.warrantyStatus ? status === filters.warrantyStatus : true;
    }).length > 0;

  const warrantyData = (
    holdings: DTHoldingProxy[]
  ): WarrantyOverviewShape[] => {
    const result: WarrantyOverviewShape[] = [];
    holdings.forEach((item) => {
      const filtered = (item.warranties || []).filter((w) => {
        const status = getWarrantyStatus(w);
        return filters.warrantyStatus
          ? status === filters.warrantyStatus
          : true;
      });
      result.push(
        ...filtered.map((warranty) => ({ ...warranty, holding: item }))
      );
    });
    return result;
  };

  const chartOptionsTemplate = useMemo(
    () =>
      withCsvHeaders(chartProducts.options, csvExportConfigs.totalWarranties, {
        fullXAxisLabels: true,
      }),
    [t]
  );

  const chartOptionsStatus = useMemo(
    () =>
      withCsvHeaders(chartProducts.options, csvExportConfigs.warrantyStatus, {
        fullXAxisLabels: true,
      }),
    [t]
  );

  const WarrantyTable = useMemo(
    () =>
      CreateTable<WarrantyOverviewShape>(
        warrantyData(holdingList.data || []),
        (
          [
            {
              header: 'ID',
              value: (data) => data.warrantyLink?.slice(0, 5).toUpperCase(),
            },
            {
              header: t('general.customerName'),
              value: (data) => data.holding.consumer_name || '-',
            },
            config.CLAIMS.CLAIM_DETAIL_VIEW.TYPE ===
            Config_ClaimDetailViewType.CAR_DETAILS
              ? {
                  header: 'Plate',
                  value: (data) =>
                    BL_Warranty.getVehicleRegistrationNo(
                      data.holding.self_registration?.srvInfo
                        ?.productInfo as VehicleInfo
                    ),
                }
              : undefined,
            {
              header: t('general.registered'),
              type: 'Date',
              value: (data) => {
                return data.registeredTime || data.holding.register_time;
              },
              filterVariant: 'date-range',
              muiFilterDatePickerProps: {
                format: 'DD/MM/YYYY',
              },
            },
            {
              header: t('general.plan'),
              value: (data) => {
                if (
                  data?.templateTitle &&
                  data.templateTitle === 'Standard Warranty' &&
                  i18next.exists('general.standardWarranty')
                ) {
                  return t('general.standardWarranty');
                } else {
                  return data?.templateTitle || '-';
                }
              },
              filterVariant: 'multi-select',
            },
            ...(config.WARRANTIES.SHOW_CLAIM_LIMIT
              ? [
                  {
                    header: t('general.claimLimit'),
                    value: (data: any) => {
                      const voided = data?.status === WarrantyStatus.VOIDED;
                      return voided
                        ? '-'
                        : getClaimLimit(
                            data,
                            tenantInfo?.currency,
                            CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT
                          ) || '-';
                    },
                  },
                ]
              : []),
            {
              header: t('general.start'),
              type: 'Date',
              value: (data) => data?.startDate,
              filterVariant: 'date-range',
              muiFilterDatePickerProps: {
                format: 'DD/MM/YYYY',
              },
            },
            {
              header: t('general.term'),
              filterVariant: 'select',
              filterFn: 'equals',
              value: (data) =>
                data.duration
                  ? data.duration.amount + ' ' + data.duration.division
                  : '-',
            },
            {
              header: t('general.expires'),
              type: 'Date',
              value: (data) => data?.endDate,
              filterVariant: 'date-range',
              muiFilterDatePickerProps: {
                format: 'DD/MM/YYYY',
              },
            },
            {
              header: t('general.partners'),
              filterVariant: 'select',
              filterFn: 'equals',
              value: (data) => data?.retailerName || '-',
            },
            {
              header: t('general.status'),
              type: 'Status',
              value: (data) => {
                const status = getWarrantyStatus(data);
                if (!status) return '-';
                return t(`general.${status}`) || '-';
              },
            },
          ] as TableFields<WarrantyOverviewShape>
        ).filter((field) => field !== undefined)
      ),
    [holdingList.data, isLoading, filters, t]
  );

  useEffect(() => {
    const templateData = (holdings: DTHoldingProxy[]) => {
      const totalLabel: any = {};
      holdings.filter(warrantyFilters).forEach((holding) => {
        holding.warranties.forEach((warranty) => {
          totalLabel[warranty?.templateTitle || '-'] =
            (totalLabel[warranty?.templateTitle || '-'] || 0) + 1;
        });
      });
      return Object.keys(totalLabel).map((item, index) => ({
        id: index,
        count: totalLabel[item],
        label: titleCase(item),
      }));
    };

    setChartTemplateData(
      formatDataChart(
        templateData(holdingList?.data || []),
        'series',
        'Total count',
        5
      )
    );

    const statusData = (holdings: DTHoldingProxy[]) => {
      const totalStatus: any = {};
      holdings.filter(warrantyFilters).forEach((holding) => {
        holding.warranties.forEach((warranty) => {
          const status = getWarrantyStatus(warranty);
          if (status) totalStatus[status] = (totalStatus[status] || 0) + 1;
        });
      });
      return Object.keys(totalStatus).map((item, index) => ({
        id: index,
        count: totalStatus[item],
        label: titleCase(item),
      }));
    };

    setChartStatusData(
      formatDataChart(
        statusData(holdingList?.data || []),
        'series',
        'Total count',
        5
      )
    );

    setTotal(
      holdingList?.data
        ?.filter(warrantyFilters)
        .map((i) => i.warranties.length)
        .reduce((a, b) => a + b, 0) || 0
    );
  }, [holdingList?.data, isLoading, filters]);

  // Hide PNG/SVG export options from ApexCharts toolbar (keep CSV only)
  useEffect(() => {
    const styleId = 'apexcharts-hide-image-exports';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML =
        '.apexcharts-menu-item.exportSVG, .apexcharts-menu-item.exportPNG { display: none !important; }';
      document.head.appendChild(style);
    }
  }, []);

  if (!userPermissions?.includes(Permissions.Owner)) navigate('/');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <CardWrapperWithHeader
          title={t('general.totalWarranties')}
          headerRight={<div className="text-xl font-bold">Total: {total}</div>}
          >
          <Chart
            options={chartOptionsTemplate}
            series={chartTemplateData}
            type="bar"
            height={'250px'}
          />
        </CardWrapperWithHeader>
        <CardWrapperWithHeader
          title={t('general.warrantyStatus')}
          headerRight={<div className="text-xl font-bold">Total: {total}</div>}
        >
          <Chart
            options={chartOptionsStatus}
            series={chartStatusData}
            type="bar"
            height={'250px'}
          />
        </CardWrapperWithHeader>
      </div>

      <div className="relative z-10 flex w-full items-center justify-between">
        {!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE && (
          <div className="relative md:absolute md:top-[34px] md:right-5">
            <ButtonIcon
              label={t('general.filters')}
              iconLeft={true}
              Icon={FunnelIcon}
              onClick={() => setIsFilterModalOpen(true)}
              kind={'bgLightGreen'}
              count={
                (filters && filters.beginDate) || (filters && filters.endDate)
                  ? 1
                  : 0
              }
            />
          </div>
        )}

        {config.HOLDINGS.REGISTRATION.ADVANCED_TABLE &&
          config.HOLDINGS.REGISTRATION.SHOW_MONTHLY_DATA && (
            <div className="relative pb-4 md:absolute md:left-[140px] md:top-[34px]">
              <ButtonIcon
                label={t('general.filters')}
                iconLeft={true}
                Icon={FunnelIcon}
                onClick={() => setIsFilterModalOpen(true)}
                kind={'bgLightGreen'}
                count={
                  (filters && filters.beginDate) || (filters && filters.endDate)
                    ? 1
                    : 0
                }
              />
            </div>
          )}

        {isFilterModalOpen && (
          <div
            className={`absolute ${
              config.HOLDINGS.REGISTRATION.ADVANCED_TABLE &&
              config.HOLDINGS.REGISTRATION.SHOW_MONTHLY_DATA
                ? 'left-0'
                : 'right-0'
            } top-16 z-10`}
          >
            <FiltersModal
              handleClose={() => setIsFilterModalOpen(false)}
              data={filters}
              onChange={onChangeFilters}
              option={{ showWarrantyStatus: true }}
            />
          </div>
        )}
      </div>

      <div className="relative z-0 w-full items-center justify-between py-4">
        <WarrantyTable
          initialState={{
            showGlobalFilter: true,
            sorting: [
              {
                id: 'registered',
                desc: true,
              },
            ],
          }}
          muiSearchTextFieldProps={{
            placeholder: t('general.searchByWarrantyIDOrCustomerName'),
          }}
          state={{
            isLoading,
          }}
          enableColumnFilters={Boolean(
            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
          )}
          enableTopToolbar={!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
          advancedTable={Boolean(config.HOLDINGS.REGISTRATION.ADVANCED_TABLE)}
          enableRowSelection={Boolean(
            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
          )}
          enableBottomToolbar={true}
          enableToolbarInternalActions={Boolean(
            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
          )}
          enableFacetedValues={Boolean(
            config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
          )}
          columnFilterDisplayMode="popover"
          onClickRow={(data) =>
            ONCE(
              () =>
                navigate(
                  SAGE_ROUTE_NAME.WARRANTY_OVERVIEW + '/' + data?.holding.docid,
                  {
                    state: {
                      title: (
                        data?.holding?.docid?.slice(0, 5) || ''
                      ).toUpperCase(),
                      link: data?.holding.docid,
                      warrantyLink: data.warrantyLink,
                    },
                  }
                ),
              2000
            )()
          }
        />
      </div>
    </LocalizationProvider>
  );
}
